/**
 * Product Description Reorganizer
 * Moves description content below price and creates individual accordions for each h5 section
 */

class ProductDescriptionReorganizer {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.reorganize());
    } else {
      this.reorganize();
    }
  }

  reorganize() {
    // Find the description accordion
    const descriptionAccordion = this.findDescriptionAccordion();
    if (!descriptionAccordion) {
      console.log('Description accordion not found');
      return;
    }

    // Find the price block
    const priceBlock = document.querySelector('.product__block--price');
    if (!priceBlock) {
      console.log('Price block not found');
      return;
    }

    // Get the content div
    const contentDiv = descriptionAccordion.querySelector('.accordion-details__content');
    if (!contentDiv) {
      console.log('Content div not found');
      return;
    }

    // Parse the content
    const parsedContent = this.parseContent(contentDiv);
    
    // Move description content below price block
    if (parsedContent.descriptionHtml) {
      this.insertDescriptionAfterPrice(priceBlock, parsedContent.descriptionHtml);
    }

    // Create individual accordions for each h5 section
    if (parsedContent.sections.length > 0) {
      this.createSectionAccordions(descriptionAccordion, parsedContent.sections);
    }

    // Hide the original description accordion content
    descriptionAccordion.style.display = 'none';
  }

  findDescriptionAccordion() {
    // Find accordion with "Description" heading
    const accordions = document.querySelectorAll('.accordion-details');
    for (const accordion of accordions) {
      const heading = accordion.querySelector('.h4');
      if (heading && heading.textContent.trim().toLowerCase() === 'description') {
        return accordion;
      }
    }
    return null;
  }

  parseContent(contentDiv) {
    const result = {
      descriptionHtml: '',
      sections: []
    };

    // Clone the content to parse
    const tempDiv = contentDiv.cloneNode(true);
    
    // Find the split point (either <!-- split --> comment or first h5)
    const h5Elements = tempDiv.querySelectorAll('h5');
    
    if (h5Elements.length === 0) {
      // No h5 sections, just use all content as description
      result.descriptionHtml = tempDiv.innerHTML;
      return result;
    }

    // Find the first h5
    const firstH5 = h5Elements[0];
    
    // Get description content (everything before first h5)
    const descriptionContent = [];
    let currentNode = tempDiv.firstChild;
    
    while (currentNode && currentNode !== firstH5) {
      // Check if it's a comment node with "split" text
      if (currentNode.nodeType === Node.COMMENT_NODE && currentNode.textContent.trim() === 'split') {
        break;
      }
      
      if (currentNode.nodeType === Node.ELEMENT_NODE || 
          (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim())) {
        descriptionContent.push(currentNode.cloneNode(true));
      }
      currentNode = currentNode.nextSibling;
    }

    // Create description HTML
    const descDiv = document.createElement('div');
    descriptionContent.forEach(node => descDiv.appendChild(node));
    result.descriptionHtml = descDiv.innerHTML.trim();

    // Parse h5 sections
    h5Elements.forEach((h5, index) => {
      const sectionTitle = h5.textContent.trim();
      const sectionContent = [];
      let nextNode = h5.nextSibling;
      
      // Get content until next h5 or end
      while (nextNode && nextNode.tagName !== 'H5') {
        if (nextNode.nodeType === Node.ELEMENT_NODE || 
            (nextNode.nodeType === Node.TEXT_NODE && nextNode.textContent.trim())) {
          sectionContent.push(nextNode.cloneNode(true));
        }
        nextNode = nextNode.nextSibling;
      }

      const sectionDiv = document.createElement('div');
      sectionContent.forEach(node => sectionDiv.appendChild(node));
      
      result.sections.push({
        title: sectionTitle,
        html: sectionDiv.innerHTML.trim(),
        icon: this.getIconForSection(sectionTitle)
      });
    });

    return result;
  }

  getIconForSection(title) {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('key features')) {
      return 'heart';
    } else if (titleLower.includes('details') || titleLower.includes('dimensions')) {
      return 'ruler';
    } else if (titleLower.includes('shipping') || titleLower.includes('delivery')) {
      return 'package';
    } else if (titleLower.includes('warranty')) {
      return 'certificate';
    } else {
      return 'check-circle';
    }
  }

  getIconSvg(iconName) {
    const icons = {
      'heart': `<path d="M10 17.5C10 17.5 1.875 13.125 1.875 7.96875C1.875 6.84987 2.31947 5.77681 3.11064 4.98564C3.90181 4.19447 4.97487 3.75 6.09375 3.75C7.85859 3.75 9.37031 4.71172 10 6.25C10.6297 4.71172 12.1414 3.75 13.9062 3.75C15.0251 3.75 16.0982 4.19447 16.8894 4.98564C17.6805 5.77681 18.125 6.84987 18.125 7.96875C18.125 13.125 10 17.5 10 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
      'ruler': `<path d="M12.943 2.0589L2.05913 12.9428C1.81505 13.1869 1.81505 13.5826 2.05913 13.8267L6.17526 17.9428C6.41934 18.1869 6.81507 18.1869 7.05915 17.9428L17.9431 7.05892C18.1871 6.81485 18.1871 6.41912 17.9431 6.17504L13.8269 2.0589C13.5829 1.81483 13.1871 1.81483 12.943 2.0589Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 5L12.5 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.5 7.5L10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5 10L7.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
      'package': `<path d="M10 10.0859V18.1234" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2.55469 6.00977L10 10.0848L17.4453 6.00977" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M17.175 14.2849L10.3 18.049C10.208 18.0993 10.1049 18.1257 10 18.1257C9.89515 18.1257 9.79198 18.0993 9.7 18.049L2.825 14.2849C2.72683 14.2312 2.64488 14.1521 2.58772 14.0559C2.53055 13.9597 2.50025 13.8499 2.5 13.738V6.2646C2.50025 6.1527 2.53055 6.04291 2.58772 5.94672C2.64488 5.85052 2.72683 5.77144 2.825 5.71772L9.7 1.95366C9.79198 1.90333 9.89515 1.87695 10 1.87695C10.1049 1.87695 10.208 1.90333 10.3 1.95366L17.175 5.71772C17.2732 5.77144 17.3551 5.85052 17.4123 5.94672C17.4695 6.04291 17.4997 6.1527 17.5 6.2646V13.7365C17.5 13.8486 17.4699 13.9587 17.4127 14.0552C17.3555 14.1517 17.2734 14.2311 17.175 14.2849Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.37109 3.77344L13.7492 7.81172V11.8742" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
      'certificate': `<path d="M5.625 10.625H9.375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.625 8.125H9.375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15.3125 13.125C17.211 13.125 18.75 11.586 18.75 9.6875C18.75 7.78902 17.211 6.25 15.3125 6.25C13.414 6.25 11.875 7.78902 11.875 9.6875C11.875 11.586 13.414 13.125 15.3125 13.125Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.125 15H3.125C2.95924 15 2.80027 14.9342 2.68306 14.8169C2.56585 14.6997 2.5 14.5408 2.5 14.375V4.375C2.5 4.20924 2.56585 4.05027 2.68306 3.93306C2.80027 3.81585 2.95924 3.75 3.125 3.75H16.875C17.0408 3.75 17.1997 3.81585 17.3169 3.93306C17.4342 4.05027 17.5 4.20924 17.5 4.375V7.03594" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M13.125 12.3398V17.5008L15.3125 16.2508L17.5 17.5008V12.3398" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
      'check-circle': `<path d="M6.875 10.625L8.75 12.5L13.125 8.125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
    };

    return icons[iconName] || icons['check-circle'];
  }

  insertDescriptionAfterPrice(priceBlock, descriptionHtml) {
    // Create a description block
    const descBlock = document.createElement('div');
    descBlock.className = 'product__block product__block--description rte';
    descBlock.innerHTML = descriptionHtml;
    
    // Insert after price block
    priceBlock.parentNode.insertBefore(descBlock, priceBlock.nextSibling);
  }

  createSectionAccordions(originalAccordion, sections) {
    const parentBlock = originalAccordion.closest('.product__block');
    
    sections.forEach((section, index) => {
      const accordionBlock = this.createAccordionElement(section, index);
      
      // Insert after the original accordion block
      parentBlock.parentNode.insertBefore(accordionBlock, parentBlock.nextSibling);
    });
  }

  createAccordionElement(section, index) {
    const blockDiv = document.createElement('div');
    blockDiv.className = 'product__block product__block--collapsible_tab';
    
    const details = document.createElement('details');
    details.id = `Details-reorganized-${index}-${Date.now()}`;
    details.className = 'accordion-details';
    details.setAttribute('is', 'accordion-group');
    details.setAttribute('aria-expanded', 'false');
    
    const summary = document.createElement('summary');
    summary.className = 'accordion-details__summary flex items-center justify-between';
    
    const iconDiv = document.createElement('div');
    iconDiv.className = 'flex items-center gap-2';
    
    // Create icon SVG
    const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    iconSvg.setAttribute('class', `icon icon-${section.icon} non-scaling-stroke icon--medium`);
    iconSvg.setAttribute('aria-hidden', 'true');
    iconSvg.setAttribute('focusable', 'false');
    iconSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    iconSvg.setAttribute('width', '20');
    iconSvg.setAttribute('height', '20');
    iconSvg.setAttribute('viewBox', '0 0 20 20');
    iconSvg.setAttribute('fill', 'none');
    iconSvg.innerHTML = this.getIconSvg(section.icon);
    
    const heading = document.createElement('h2');
    heading.className = 'h4 inline-richtext';
    heading.textContent = section.title;
    
    iconDiv.appendChild(iconSvg);
    iconDiv.appendChild(heading);
    
    // Create plus toggle icon
    const plusIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    plusIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    plusIcon.setAttribute('class', 'icon icon-plus-toggle icon--medium');
    plusIcon.setAttribute('viewBox', '0 0 20 20');
    plusIcon.setAttribute('fill', 'none');
    plusIcon.innerHTML = `
      <path class="horizontal" d="M3.125 10H16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      <path class="vertical" d="M10 3.125L10 16.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
    `;
    
    summary.appendChild(iconDiv);
    summary.appendChild(plusIcon);
    
    const contentDiv = document.createElement('div');
    contentDiv.className = 'accordion-details__content rte';
    contentDiv.innerHTML = section.html;
    
    details.appendChild(summary);
    details.appendChild(contentDiv);
    blockDiv.appendChild(details);
    
    return blockDiv;
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  new ProductDescriptionReorganizer();
}

